import { useState } from "react";
import { useAuth } from "@/App";
import { supabase, createTimelineEvent } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Users, UserPlus, Mail, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const PartiesPanel = ({ transactionId, participants, onUpdate, isAgent }) => {
  const { user } = useAuth();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("buyer");

  // Edit state
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editName, setEditName] = useState("");

  const handleAddParty = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const existing = participants.find(p => p.email === email);
      if (existing) {
        toast.error("This email is already added to the transaction");
        return;
      }

      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('email', email)
        .single();

      const { error } = await supabase
        .from('transaction_participants')
        .insert({
          transaction_id: transactionId,
          user_id: existingProfile?.id || null,
          email: email,
          role: role,
          status: existingProfile ? 'active' : 'pending'
        });

      if (error) throw error;

      await createTimelineEvent(
        transactionId,
        'participant_added',
        `${role.charAt(0).toUpperCase() + role.slice(1)} added: ${email}`
      );

      toast.success(`${role.charAt(0).toUpperCase() + role.slice(1)} added successfully`);
      setAddDialogOpen(false);
      setEmail("");
      setRole("buyer");
      onUpdate();
    } catch (error) {
      console.error('Error adding party:', error);
      toast.error(error.message || "Failed to add party");
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (participant) => {
    setEditingParticipant(participant);
    setEditEmail(participant.email || "");
    setEditRole(participant.role || "buyer");
    setEditName(participant.profiles?.full_name || "");
    setEditDialogOpen(true);
  };

  const handleEditParty = async (e) => {
    e.preventDefault();
    if (!editingParticipant) return;
    setLoading(true);
    try {
      const update = { email: editEmail.trim(), role: editRole };
      const { error } = await supabase
        .from('transaction_participants')
        .update(update)
        .eq('id', editingParticipant.id);
      if (error) throw error;

      // If participant has a linked profile, update name there
      if (editingParticipant.user_id && editName.trim() !== (editingParticipant.profiles?.full_name || "")) {
        await supabase.from('profiles').update({ full_name: editName.trim() }).eq('id', editingParticipant.user_id);
      }

      toast.success("Party updated");
      setEditDialogOpen(false);
      setEditingParticipant(null);
      onUpdate();
    } catch (error) {
      console.error('Error editing party:', error);
      toast.error(error.message || "Failed to update party");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveParty = async (participant) => {
    const name = getName(participant);
    if (!confirm(`Remove ${name} from this transaction? This cannot be undone.`)) return;
    try {
      const { error, count } = await supabase
        .from('transaction_participants')
        .delete()
        .eq('id', participant.id)
        .select();
      if (error) throw error;

      await createTimelineEvent(
        transactionId,
        'participant_removed',
        `${participant.role.charAt(0).toUpperCase() + participant.role.slice(1)} removed: ${participant.email}`
      );

      toast.success("Party removed");
      onUpdate();
    } catch (error) {
      console.error('Error removing party:', error);
      toast.error(error.message || "Failed to remove party");
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'agent': return 'bg-primary/10 text-primary';
      case 'buyer': return 'bg-blue-500/10 text-blue-600';
      case 'seller': return 'bg-green-500/10 text-green-600';
      default: return 'bg-gray-500/10 text-gray-600';
    }
  };

  const getInitials = (participant) => {
    if (participant.profiles?.full_name) {
      return participant.profiles.full_name.charAt(0).toUpperCase();
    }
    return participant.email.charAt(0).toUpperCase();
  };

  const getName = (participant) => {
    if (participant.profiles?.full_name) {
      return participant.profiles.full_name;
    }
    return participant.email;
  };

  return (
    <Card className="border-border/60 shadow-soft" data-testid="parties-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <Users className="w-4 h-4" />
              Transaction Parties
            </CardTitle>
            <CardDescription className="text-sm">
              All parties involved in this transaction
            </CardDescription>
          </div>
          {isAgent && (
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-md h-8 text-sm" data-testid="add-party-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Party
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Party</DialogTitle>
                  <DialogDescription>
                    Add a buyer or seller to this transaction by email
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddParty} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="user@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="party-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger data-testid="party-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="buyer">Buyer</SelectItem>
                        <SelectItem value="seller">Seller</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={loading} data-testid="confirm-add-party">
                      {loading ? "Adding..." : "Add Party"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {participants.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No participants yet</p>
          ) : (
            participants.map((participant) => {
              const isCurrentUser = participant.user_id === user?.id;
              const canModify = isAgent && !isCurrentUser;
              return (
                <div
                  key={participant.id}
                  className="flex items-center gap-3 p-3 rounded-md border border-border/60"
                  data-testid={`party-row-${participant.id}`}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className={getRoleBadgeColor(participant.role)}>
                      {getInitials(participant)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">
                      {getName(participant)}
                      {isCurrentUser && (
                        <span className="text-xs text-muted-foreground ml-2">(You)</span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {participant.email}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {participant.status === 'pending' && (
                      <Badge variant="outline" className="text-amber-600 border-amber-200">
                        Pending
                      </Badge>
                    )}
                    <Badge variant="secondary" className="capitalize">
                      {participant.role}
                    </Badge>
                    {canModify && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-slate-600"
                          onClick={() => openEditDialog(participant)}
                          title="Edit"
                          data-testid={`edit-party-${participant.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-red-500"
                          onClick={() => handleRemoveParty(participant)}
                          title="Remove"
                          data-testid={`remove-party-${participant.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent data-testid="edit-party-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Party</DialogTitle>
            <DialogDescription>Update this participant's details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditParty} className="space-y-4 mt-4">
            {editingParticipant?.user_id && (
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Full name"
                  data-testid="edit-party-name"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                required
                data-testid="edit-party-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger data-testid="edit-party-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="buyer">Buyer</SelectItem>
                  <SelectItem value="seller">Seller</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="confirm-edit-party">
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
